import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { unitLabel } from '../../utils/testUnits';

// Coach-Owned Test Types (058) — "My Tests" manager, opened from a
// contextual action inside Team Testing itself (never a separate
// Program-settings page — that surface, and the Program-owned test
// model it managed, is retired by this refactor). One dialog handles
// list + create + edit, deliberately reusing the exact selector-driven
// field set the retired ProgramPerformanceTestsPage.js already proved
// out (name is the only free-text field) rather than inventing a
// second form shape.
//
// Archive/Restore language (never "Delete") — archiving a test with
// recorded history is the normal, expected path: it only flips
// test_types.active via update_coach_test_type()'s p_active, which never
// touches a single test_attempts row, PB, leaderboard fact, or
// correction/invalidation eligibility (058's own header). A coach test
// is never hard-deleted by this dialog.

const CATEGORY_OPTIONS = [
  { value: 'speed', label: 'Speed' },
  { value: 'agility', label: 'Agility' },
  { value: 'power', label: 'Power' },
  { value: 'other', label: 'Other' },
];
const UNIT_OPTIONS = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'inches', label: 'Inches' },
  { value: 'mph', label: 'MPH' },
  { value: 'reps', label: 'Reps' },
  { value: 'pounds', label: 'Pounds' },
];
const DIRECTION_OPTIONS = [
  { value: 'lower', label: 'Lower (fastest/smallest wins)' },
  { value: 'higher', label: 'Higher (farthest/most wins)' },
];

function categoryLabel(value) {
  return CATEGORY_OPTIONS.find(o => o.value === value)?.label || value;
}

function emptyForm() {
  return { displayName: '', category: 'other', unit: 'seconds', betterDirection: 'lower', supportsAppStopwatch: false, recommendedAttempts: 3 };
}

/** Selector-driven fields shared by Create and Edit — minimizes free
 * text to Name only. `lockSemantics` is not pre-computed here (no extra
 * query for it) — update_coach_test_type() is the actual authority and
 * rejects a semantic change once any attempt exists, surfaced as an
 * ordinary save error; this component never blocks the fields itself. */
function TestTypeFields({ form, onChange }) {
  const stopwatchAllowed = form.unit === 'seconds';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        label="Test Name" size="small" autoFocus fullWidth
        value={form.displayName}
        onChange={e => onChange({ ...form, displayName: e.target.value })}
      />
      <FormControl size="small" fullWidth>
        <InputLabel id="ctm-category-label">Category</InputLabel>
        <Select
          labelId="ctm-category-label" label="Category"
          value={form.category}
          onChange={e => onChange({ ...form, category: e.target.value })}
        >
          {CATEGORY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth>
        <InputLabel id="ctm-unit-label">Measurement Unit</InputLabel>
        <Select
          labelId="ctm-unit-label" label="Measurement Unit"
          value={form.unit}
          onChange={e => {
            const unit = e.target.value;
            onChange({ ...form, unit, supportsAppStopwatch: unit === 'seconds' ? form.supportsAppStopwatch : false });
          }}
        >
          {UNIT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth>
        <InputLabel id="ctm-direction-label">Better Result</InputLabel>
        <Select
          labelId="ctm-direction-label" label="Better Result"
          value={form.betterDirection}
          onChange={e => onChange({ ...form, betterDirection: e.target.value })}
        >
          {DIRECTION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth disabled={!stopwatchAllowed}>
        <InputLabel id="ctm-stopwatch-label">FFN Stopwatch</InputLabel>
        <Select
          labelId="ctm-stopwatch-label" label="FFN Stopwatch"
          value={stopwatchAllowed ? String(form.supportsAppStopwatch) : 'false'}
          onChange={e => onChange({ ...form, supportsAppStopwatch: e.target.value === 'true' })}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Recommended Attempts" size="small" type="number" fullWidth
        inputProps={{ min: 1, step: 1 }}
        value={form.recommendedAttempts}
        onChange={e => onChange({ ...form, recommendedAttempts: e.target.value })}
      />
    </Box>
  );
}

function TestForm({ form, onChange, onCancel, onSave, saving, error, saveLabel }) {
  return (
    <>
      <DialogContent>
        <TestTypeFields form={form} onChange={onChange} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>Cancel</Button>
        <Button onClick={onSave} disabled={saving} variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : saveLabel}
        </Button>
      </DialogActions>
    </>
  );
}

/**
 * CoachTestManagerDialog — "My Tests." List view shows this coach's own
 * tests (active AND archived, per Team Testing's own resolver excluding
 * archived tests from availability but this dialog still needing to
 * show them so they can be restored), each with Edit + Archive/Restore.
 * "+ Create Test" switches into the shared form for a brand-new test.
 * Router-free, props/callbacks only (onCreate/onUpdate injected), same
 * testability discipline as TeamTestingStation.js itself.
 */
export default function CoachTestManagerDialog({ open, myTests, onClose, onCreate, onUpdate }) {
  const [mode, setMode] = useState('list'); // 'list' | 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    if (open) {
      setMode('list');
      setEditTarget(null);
      setError(null);
    }
  }, [open]);

  const openCreate = () => {
    setForm(emptyForm());
    setError(null);
    setMode('create');
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setForm({
      displayName: t.display_name,
      category: t.category,
      unit: t.unit,
      betterDirection: t.better_direction,
      supportsAppStopwatch: t.supports_app_stopwatch,
      recommendedAttempts: t.recommended_attempts,
    });
    setError(null);
    setMode('edit');
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        displayName: form.displayName,
        category: form.category,
        unit: form.unit,
        betterDirection: form.betterDirection,
        supportsAppStopwatch: form.supportsAppStopwatch,
        recommendedAttempts: Number(form.recommendedAttempts) || 3,
      });
      setMode('list');
    } catch (e) {
      setError(e.message || 'Could not create that test.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onUpdate({
        testTypeKey: editTarget.key,
        displayName: form.displayName,
        category: form.category,
        recommendedAttempts: Number(form.recommendedAttempts) || 1,
        unit: form.unit,
        betterDirection: form.betterDirection,
        supportsAppStopwatch: form.supportsAppStopwatch,
      });
      setMode('list');
    } catch (e) {
      setError(e.message || 'Could not save that test.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t) => {
    setBusyKey(t.key);
    setError(null);
    try {
      await onUpdate({ testTypeKey: t.key, active: !t.active });
    } catch (e) {
      setError(e.message || 'Could not update that test.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="xs" fullWidth>
      <DialogTitle>My Tests</DialogTitle>
      {mode === 'list' && (
        <>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {(myTests || []).length === 0 ? (
              <Typography sx={{ color: '#6a8aa8', py: 1 }}>You haven't created any custom tests yet.</Typography>
            ) : (
              myTests.map(t => (
                <Box key={t.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {t.display_name}
                      {!t.active && <Chip size="small" label="Archived" sx={{ ml: 1, bgcolor: '#131318', color: '#6a8aa8', fontWeight: 700 }} />}
                    </Typography>
                    <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>
                      {categoryLabel(t.category)} · {unitLabel(t.unit)} · {t.better_direction === 'lower' ? 'Lower is better' : 'Higher is better'}
                      {t.supports_app_stopwatch ? ' · FFN Stopwatch' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" onClick={() => openEdit(t)} sx={{ textTransform: 'none', fontWeight: 700 }}>Edit</Button>
                    <Button
                      size="small" onClick={() => handleToggleActive(t)} disabled={busyKey === t.key}
                      color={t.active ? 'secondary' : 'primary'} sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {busyKey === t.key ? <CircularProgress size={14} /> : (t.active ? 'Archive Test' : 'Restore Test')}
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6a8aa8' }}>Close</Button>
            <Button onClick={openCreate} variant="outlined" color="primary" sx={{ textTransform: 'none', fontWeight: 700 }}>
              + Create Test
            </Button>
          </DialogActions>
        </>
      )}
      {mode === 'create' && (
        <TestForm form={form} onChange={setForm} onCancel={() => setMode('list')} onSave={handleCreate} saving={saving} error={error} saveLabel="Create Test" />
      )}
      {mode === 'edit' && editTarget && (
        <TestForm form={form} onChange={setForm} onCancel={() => setMode('list')} onSave={handleSaveEdit} saving={saving} error={error} saveLabel="Save Changes" />
      )}
    </Dialog>
  );
}
